import React from 'react';

import { useIntl } from 'react-intl';
import { queryClient } from 'src/lib/react-query';
import ConfirmationModal from 'src/registry/components/blocks/ConfirmationModal';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { ICON_MAP } from 'src/utils/constants';
import { toast } from 'src/hooks/useToast';
import { useDeleteBillingEntityBankAccount } from 'src/features/billingEntityBankAccounts/api/useDeleteBillingEntityBankAccount';
import { LIST_BILLING_ENTITY_BANK_ACCOUNTS_RESOURCE } from 'src/features/billingEntityBankAccounts/api/useListBillingEntityBankAccounts';
import { bankAccountMessages } from 'src/features/billingEntityBankAccounts/utils/messages';
import { BankAccount } from 'src/features/billingEntityBankAccounts/utils/types';
import { useOnError } from 'src/utils/errors';

interface DeleteBillingEntityBankAccountFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedBankAccount: BankAccount;
    children?: React.ReactNode;
}

function DeleteBillingEntityBankAccountFormModal<TData>({
    selectedBankAccount,
    children,
}: DeleteBillingEntityBankAccountFormModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { formatMessage } = useIntl();
    const onError = useOnError();

    const { mutate: deleteBankAccount } = useDeleteBillingEntityBankAccount({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [LIST_BILLING_ENTITY_BANK_ACCOUNTS_RESOURCE] });
            setIsModalOpen(false);
            toast({
                title: formatMessage(bankAccountMessages.deleteBankAccount),
                description: formatMessage(bankAccountMessages.deleteBankAccountSuccessDescription),
            });
        },
        onError: (error) => {
            onError(error, () => null);
        },
    });

    return (
        <ConfirmationModal
            isModalOpen={isModalOpen}
            setIsModalOpen={setIsModalOpen}
            dialogDetails={{
                title: { ...bankAccountMessages.deleteBankAccount },
                subTitle: {
                    ...bankAccountMessages.deleteBankAccountSubtitle,
                    values: {
                        entity: selectedBankAccount.name,
                    },
                },
                icon: ICON_MAP.DELETE,
                confirmationMessage: { ...bankAccountMessages.confirmDeleteBankAccount },
            }}
            callbackButton={{
                variant: 'destructiveLight',
                buttonTitle: bankAccountMessages.deleteBankAccount,
                callbackFn: () => {
                    return (
                        deleteBankAccount({
                            bankAccountId: selectedBankAccount.id,
                        }),
                        setIsModalOpen(false)
                    );
                },
            }}
            children={children}
        />
    );
}

export default DeleteBillingEntityBankAccountFormModal;
